import React from 'react';
import { motion } from 'framer-motion';
import IndustryPageHeading from '../components/IndustryPageHeading';
import EnterpriseVideo from '../components/EnterpriseVideo';

/**
 * Healthcare Solutions — internal route /solutions/industries/healthcare
 *
 * Three content sections with alternating layout direction (text/video),
 * mirroring the first three sections of the Logistics Solutions page and
 * matching the Insurance / Real Estate / Retail industry subpages. Each
 * video slot is an empty champagne-gold-bordered placeholder frame
 * labeled "Video Demo Coming Soon", alternating sides (video right, then
 * left, then right). After the three sections the page goes directly to
 * the site's standard footer, which is rendered globally by Layout.
 */

const VideoDemoFrame = ({ cloudflareVideoId }) => {
  if (!cloudflareVideoId) {
    return (
      <div className="w-full">
        <div
          className="relative w-full overflow-hidden rounded-lg bg-gradient-to-br from-[#E8E4DC] to-[#E8EDE7]"
          style={{
            aspectRatio: '16 / 9',
            border: '2px solid #D4AF37',
            boxShadow:
              'inset 0 2px 8px rgba(20, 30, 48, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1)',
          }}
          data-testid="healthcare-video-placeholder"
        >
          <div className="absolute inset-0 flex items-center justify-center">
            <p className="text-base sm:text-lg text-black/60 font-medium tracking-wide">
              Video Demo Coming Soon
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <EnterpriseVideo
        cloudflareVideoId={cloudflareVideoId}
        title="Healthcare demo video"
        testId={`healthcare-video-${cloudflareVideoId}`}
        className="w-full rounded-lg bg-gradient-to-br from-[#E8E4DC] to-[#E8EDE7]"
        style={{
          aspectRatio: '16 / 9',
          border: '2px solid #D4AF37',
          boxShadow:
            'inset 0 2px 8px rgba(20, 30, 48, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1)',
        }}
      />
    </div>
  );
};

const FeatureBlock = ({ title, description }) => (
  <div className="pl-4 relative">
    <div
      className="absolute left-0 top-1 bottom-1 w-0.5 rounded-full"
      style={{
        background: 'linear-gradient(to bottom, #D4AF37, rgba(212, 175, 55, 0.4))',
      }}
    />
    <h3 className="text-base sm:text-lg font-medium text-black/90">{title}</h3>
    <p className="text-sm text-black/70 mt-1">{description}</p>
  </div>
);

const TextContent = ({ eyebrow, heading, body, features, isPageHeading }) => {
  const HeadingTag = isPageHeading ? 'h1' : 'h2';
  return (
    <div>
      <p className="text-sm md:text-base font-bold text-black/90 mb-3 md:mb-4 uppercase tracking-wider">
        {eyebrow}
      </p>
      <HeadingTag className="text-3xl md:text-4xl font-medium text-black/90 mb-4 md:mb-6">
        {heading}
      </HeadingTag>
      <p className="text-base sm:text-lg text-black/70 mb-8 md:mb-10">{body}</p>
      <div className="space-y-6">
        {features.map((f) => (
          <FeatureBlock key={f.title} title={f.title} description={f.description} />
        ))}
      </div>
    </div>
  );
};

const sections = [
  {
    eyebrow: 'Patient Engagement',
    cloudflareVideoId: '5337b03434532c27ba088b330570ff31',
    heading: 'Every patient question, answered without the hold music.',
    body:
      'GoogLyn handles appointment scheduling, intake forms, and routine questions around the clock \u2014 so front-desk staff spend time with the patients in front of them, not the phone.',
    features: [
      {
        title: 'Automated scheduling & reminders',
        description: 'Appointments booked and confirmed without a phone call.',
      },
      {
        title: '24/7 patient support',
        description: 'Common questions answered instantly, any time of day.',
      },
      {
        title: 'Digital intake automation',
        description: 'Forms captured and organized before the visit even starts.',
      },
    ],
  },
  {
    eyebrow: 'Clinical & Administrative Operations',
    cloudflareVideoId: '5a0dd62bb3a3d041a62d5ccddabeb1c6',
    heading: 'Records and billing, handled in the background.',
    body:
      'GoogLyn organizes patient records, processes claims, and coordinates care handoffs automatically \u2014 reducing the administrative load that pulls clinical staff away from patients.',
    features: [
      {
        title: 'Automated records management',
        description: 'Patient data organized and accessible instantly.',
      },
      {
        title: 'Streamlined billing & claims',
        description: 'Claims processed and submitted without manual entry.',
      },
      {
        title: 'Coordinated care handoffs',
        description: 'Referrals and provider transitions tracked automatically.',
      },
    ],
  },
  {
    eyebrow: 'Compliance & Reporting',
    cloudflareVideoId: '162682e6152a710ddfba2f11ae8a9ab8',
    heading: 'Every record, always audit-ready.',
    body:
      'Compliance monitoring, fraud detection, and reporting run continuously in the background, so administrators work from real-time data instead of scrambling before an audit.',
    features: [
      {
        title: 'Continuous compliance monitoring',
        description: 'Audit trails stay current automatically.',
      },
      {
        title: 'Real-time reporting',
        description: 'Patient and operational data always at your fingertips.',
      },
      {
        title: 'Fraud detection',
        description: 'Anomalies flagged before they become liabilities.',
      },
    ],
  },
];

const IndustryHealthcare = () => {
  return (
    <div className="w-full" data-testid="healthcare-solutions-page">
      <IndustryPageHeading name="Healthcare" testId="healthcare-page-heading" />
      {sections.map((section, idx) => {
        const textFirst = idx % 2 === 0; // sections 1,3 → text left (video right); 2 → video left
        const bg = idx % 2 === 0 ? 'bg-white' : 'bg-[#F5F7F5]';
        return (
          <section
            key={section.eyebrow}
            className={`py-16 md:py-24 ${bg}`}
            data-testid={`healthcare-section-${idx + 1}`}
          >
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center"
              >
                {textFirst ? (
                  <>
                    <TextContent {...section} isPageHeading={idx === 0} />
                    <VideoDemoFrame cloudflareVideoId={section.cloudflareVideoId} />
                  </>
                ) : (
                  <>
                    {/* Video first on desktop; text stays above video on mobile for readability */}
                    <div className="order-2 lg:order-1">
                      <VideoDemoFrame cloudflareVideoId={section.cloudflareVideoId} />
                    </div>
                    <div className="order-1 lg:order-2">
                      <TextContent {...section} isPageHeading={idx === 0} />
                    </div>
                  </>
                )}
              </motion.div>
            </div>
          </section>
        );
      })}
    </div>
  );
};

export default IndustryHealthcare;
